/**
 * Lane C0-PROPS round 2: the dense dressing of the 256 m plains, at the level of the user's sample scenes
 * (biomes-kit-sheet-2: 03 trade-town market, 13 training ground, 14 duel arena, 02 gate; ideas only). Laid on top of
 * the shared placement (kit/placement.ts, data/layouts/plains.ts `PLAINS_PROPS`, lane C0-P1).
 *
 * Map metres. `solid` marks what should block walking: a request to C0-P1 to move those into the shared
 * `PLAINS_PROPS` (whose solid entries the rules lay as blocking); until then they are drawn only.
 * Tall pieces stay off the near (screen-down) side of fighting spaces: the plaza R 12, the ring R 8 and the court
 * (scripts/lanes/c0-props/occlusion.mts checks it).
 */
import { hash2, plainsLayout } from '@nine/shared';
import type { PropId } from './props';

export interface Dressed {
  readonly id: PropId;
  readonly at: readonly [number, number];
  readonly solid?: boolean;
  /** It stands on water (lily pads, boats). */
  readonly water?: boolean;
}

function build(): Dressed[] {
  const out: Dressed[] = [];
  const put = (id: PropId, x: number, y: number, solid = false, water = false) => out.push({ id, at: [x, y], solid, water });

  // ── The market street (shops y 113–119 on the north, street y 120–128, stalls y 128.5–133.5 on the south). ──
  // In front of each shophouse: its sign on a post, goods at the door.
  const shops: [number, PropId, PropId][] = [
    [170, 'shopSignRed', 'pottery'],
    [178, 'shopSignDark', 'baskets'],
    [194, 'shopSignRed', 'vases'],
    [202, 'shopSignDark', 'barrelStack'],
  ];
  for (const [x, sign, goods] of shops) {
    put(sign, x - 2.2, 119.8, true);
    put(goods, x + 1.6, 119.9, true);
  }
  // The warehouse's yard: crates, sacks, barrels; a wheelbarrow.
  put('crateStack', 157.2, 114.5, true);
  put('sackStack', 157.2, 117, true);
  put('barrelStack', 166.2, 119.9, true);
  put('wheelbarrow', 160.5, 120.6, true);
  // The gap between the cloth shop and the bookshop: a canopy over a produce stand.
  put('canopyRed', 184.5, 116.5, true);
  put('produce', 184.5, 116.8);
  put('canopyCream', 188.8, 116.5, true);
  put('hangingRack', 188.8, 116.2, true);
  // Strings of lanterns and flags over the street's north edge, between the lantern posts.
  for (const x of [170.5, 180.5, 192.5, 202.5]) put(x === 180.5 ? 'flagString' : 'lanternString', x, 120.6);
  // Among the south stalls: goods, umbrellas, jars.
  put('wineJars', 167.5, 133.9, true);
  put('umbrellaRed', 174.5, 134.2);
  put('produce2', 181.8, 134);
  put('umbrellaOchre', 196.6, 134.2);
  put('sackStack', 206.6, 133.6, true);
  // Round 3, denser bays: more goods along the shop fronts and between the stalls, signs and lantern poles.
  for (const [x, id] of [
    [163.2, 'produce'],
    [168.2, 'baskets'],
    [172.4, 'pottery'],
    [176.2, 'wineJars'],
    [180.4, 'umbrellaOchre'],
    [192.4, 'produce2'],
    [196.2, 'hangingRack'],
    [200.4, 'baskets'],
    [204.6, 'pottery'],
  ] as const)
    put(id, x, 119.4, id !== 'umbrellaOchre');
  for (const [x, id] of [
    [160.4, 'crateStack'],
    [165.6, 'shopSignRed'],
    [169.6, 'baskets'],
    [173.6, 'shopSignDark'],
    [177.4, 'produce'],
    [185.2, 'barrelStack'],
    [188.6, 'shopSignRed'],
    [195.6, 'wineJars'],
    [200.6, 'shopSignDark'],
    [209.2, 'produce2'],
  ] as const)
    put(id, x, 128.9, id !== 'produce' && id !== 'produce2');
  for (const x of [162.5, 182.5, 197.5]) put('lanternPole2', x, 128.6, true);
  put('canopyCream', 210.5, 131.5, true);
  put('pottedBloom', 157.4, 128.8, true);
  put('birdCage', 191.4, 119.6, true);

  // ── The inn's frontage: tea tables under umbrellas, wine jars by the door, a sign frame. ──
  for (const x of [178.5, 183.5]) put('teaTable', x, 111.4, true);
  put('umbrellaRed', 181, 111.2);
  put('wineJars', 191.6, 110.8, true);
  put('signFrame', 187.5, 111, true);

  // ── The opera square: lantern strings round its back, pennants on the stage's corners, potted blooms. ──
  put('lanternString', 162.5, 99.2);
  put('lanternString', 169.5, 99.2);
  put('pennantRed', 160.6, 99.6, true);
  put('pennantRed', 171.6, 99.6, true);
  for (const x of [160.5, 171.5]) put('pottedBloom', x, 109.4, true);

  // ── The training court (x 73–99, y 113–125), like a training ground: targets and banners on the west and north
  // edges (behind the court on screen), plum posts and stone weights in its corners, a war drum by the hall. ──
  for (const y of [117.5, 121.5]) put('target', 73.6, y, true);
  put('bannerTRed', 76.6, 113.4, true);
  put('bannerTBlue', 96.4, 113.4, true);
  put('warDrum', 90.8, 113.6, true);
  put('plumPosts', 81.5, 115.6, true);
  put('stoneWeights', 74.4, 124.4);
  put('woodMan', 73.6, 115.2, true);
  put('woodMan', 73.6, 124.4, true);
  put('torchPost', 73.2, 113.2, true);
  put('torchPost', 99.2, 113.2, true);
  put('signFrameBlue', 83, 113.5, true);

  // ── The duel ring (104, 100), like a duel arena: banners and pennants round its back half (north-west), war drums
  // and racks behind them; nothing tall on its front half. ──
  for (const deg of [160, 185, 210, 235, 260, 285]) {
    const a = (deg * Math.PI) / 180;
    put(deg % 50 === 10 ? 'pennantRed' : 'bannerTRed', 104.5 + Math.cos(a) * 9.4, 100.5 + Math.sin(a) * 9.4, true);
  }
  for (const deg of [200, 250]) {
    const a = (deg * Math.PI) / 180;
    put('warDrum', 104.5 + Math.cos(a) * 11.2, 100.5 + Math.sin(a) * 11.2, true);
  }
  put('weaponRack', 104.5 + Math.cos(3.93) * 11.4, 100.5 + Math.sin(3.93) * 11.4, true);
  // Wooden arena fences on the back half (north and west sides), torches at its ends.
  for (const dx of [-3, -1, 1]) put('arenaFenceX', 104.5 + dx, 100.5 - 8.9, true);
  for (const dy of [-3, -1, 1]) put('arenaFenceY', 104.5 - 8.9, 100.5 + dy, true);
  put('torchPost', 104.5 + 4.6, 100.5 - 8.9, true);
  put('torchPost', 104.5 - 8.9, 100.5 + 4.6, true);
  put('brazier', 104.5 + Math.cos(2.8) * 10.4, 100.5 + Math.sin(2.8) * 10.4, true);
  put('brazier', 104.5 + Math.cos(5.0) * 10.4, 100.5 + Math.sin(5.0) * 10.4, true);

  // ── The hall terrace's front: palace lanterns between the sect banners, potted pines at the steps. ──
  for (const x of [72.5, 77.5, 90.5, 95.5]) put('palaceLantern', x + 0.5, 87.2, true);
  put('lionL', 81.0, 88.6, true);
  put('lionR', 88.0, 88.6, true);
  put('pottedPine', 79.4, 88.6, true);
  put('pottedPine', 89.6, 88.6, true);

  // ── The town gates, like a town gate: banners on T-posts and a lantern pole either side, inside the wall. ──
  for (const g of plainsLayout.GATES) {
    if (g.water) continue;
    const [x, y] = g.at;
    const vertical = g.name === 'ประตูตะวันตก' || g.name === 'ประตูตะวันออก' || g.name === 'ประตูเนินโจร';
    const inX = x < 128 ? 1.6 : -1.6;
    const inY = y < 128 ? 1.6 : -1.6;
    if (g.name === 'ประตูเหนือ') {
      for (const dx of [-5.5, 6.5]) put('bannerTRed', x + dx, y + 1.4, true);
      for (const dx of [-3.8, 4.8]) put('lanternPole2', x + dx, y + 1.8, true);
      for (const dx of [-7.5, 8.5]) put('wallTower', x + dx, y + 1.9, true);
      continue;
    }
    if (vertical) {
      put('bannerTRed', x + 0.5 + inX, y - 3.6, true);
      put('bannerTBlue', x + 0.5 + inX, y + 4.6, true);
    } else {
      put('bannerTRed', x - 3.6, y + 0.5 + inY, true);
      put('bannerTBlue', x + 4.6, y + 0.5 + inY, true);
    }
  }

  // ── Homes (x 78–118, y 141–167): flower beds and potted plants by the doors, a drying line, baskets, wine jars,
  // bamboo clumps at the backs, a wheelbarrow; the courtyard house's shrubs. ──
  put('flowerBedRed', 88, 156.4);
  put('flowerBedGold', 104, 151.2);
  put('pottedBloom', 113.4, 156.2, true);
  put('pottedPine', 116.8, 156.2, true);
  put('dryingLine', 92.5, 167.8, true);
  put('baskets', 101.6, 166.2, true);
  put('wineJars', 112.4, 168, true);
  put('wheelbarrow', 96, 157.6, true);
  put('bambooClump', 79.2, 142.6, true);
  put('bambooClump2', 93.6, 142.4, true);
  put('bambooClump', 119.4, 147.8, true);
  put('shrubPink', 108.5, 157.6);
  put('shrubWhite', 82.6, 168.6);
  put('bambooPile', 107.8, 159.6);

  // ── The town shrine's court (118, 178): an altar before the burner, a wayside shrine, shrubs. ──
  put('altar', 118.5, 175.2, true);
  put('roadShrine', 121.6, 176.4, true);
  put('shrubPink', 115.2, 180.4);

  // ── The carriage stop: the covered wagon waiting, crates for loading. ──
  put('coveredWagon', 113.5, 183, true);
  put('crateStack', 117.8, 180.6, true);

  // ── The pagoda garden (x 168–192, y 70–94): stone lanterns, a stupa, a scholar's rock, a cascade into a basin,
  // flowering shrubs and beds. ──
  put('pagodaLantern', 177.5, 85.8, true);
  put('pagodaLantern', 186.5, 85.8, true);
  put('stupa', 172.5, 76.5, true);
  put('rockPillar', 189.5, 74.5, true);
  put('cascade', 172.5, 89.5, true);
  put('stoneBasin', 175.8, 90.6, true);
  put('rocks', 190.5, 89.5, true);
  for (const [x, y, k] of [
    [170.5, 82.5, 0],
    [191.5, 80.5, 1],
    [179.5, 72.5, 2],
    [185.5, 91.5, 0],
  ] as const)
    put((['shrubPink', 'shrubWhite', 'shrubYellow'] as const)[k], x, y);
  put('flowerBedRed', 181.5, 87.8);

  // ── The lotus pond (x 154–174, y 141–155): pads and lotus, a moored sampan, a fishing boat, a dock. ──
  for (const [x, y, k] of [
    [158, 144, 0],
    [166, 143, 1],
    [171, 147, 0],
    [161, 151, 1],
    [168, 152, 0],
  ] as const)
    put(k ? 'lilyPads2' : 'lilyPads', x, y, false, true);
  put('dock', 156, 154.2, true);
  put('sampan', 157.6, 151.5, false, true);
  put('netBoat', 170.5, 144.8, false, true);

  // ── The shrine clearing and the way out: rocks and bamboo at the edges of the hub. ──
  put('rocks2', 199.5, 146.5, true);
  put('roadShrine', 207.5, 129.5, true);
  put('rocks', 66.5, 120.5, true);
  put('bambooClump2', 64.5, 136.5, true);

  // ── Shrubs and flower beds along the hub's roads (every ~7 m on Floor beside the road, deterministic). ──
  for (const road of plainsLayout.ROADS) {
    if (road.w < 3) continue;
    for (let k = 0; k + 1 < road.pts.length; k++) {
      const [ax, ay] = road.pts[k]!;
      const [bx, by] = road.pts[k + 1]!;
      const len = Math.hypot(bx - ax, by - ay);
      const nx = -(by - ay) / len;
      const ny = (bx - ax) / len;
      for (let t = 4; t < len; t += 7) {
        const x = ax + ((bx - ax) * t) / len;
        const y = ay + ((by - ay) * t) / len;
        if (Math.hypot(x - 128, y - 128) < 17 || x < 66 || x > 206 || y < 96 || y > 184) continue;
        if (Math.hypot(x - 104, y - 100) < 11 || (x > 71 && x < 101 && y > 111 && y < 127)) continue;
        if (x > 154 && x < 212 && y > 110 && y < 136) continue; // the market street and its rows
        const h = hash2(Math.round(x), Math.round(y));
        const side = h < 0.5 ? 1 : -1;
        const d = road.w / 2 + 1.2;
        put((['shrubGreen', 'shrubPink', 'flowerBedRed', 'shrubYellow', 'flowerBedGold', 'shrubWhite'] as const)[Math.floor(h * 6)]!, x + nx * d * side, y + ny * d * side);
      }
    }
  }
  // Fallen petals and leaves on the ground here and there (flat).
  for (const [x, y] of [
    [176, 80],
    [184, 78],
    [104, 150],
    [140, 104],
    [118, 138],
  ] as const)
    put(x > 170 ? 'petals' : 'leaves', x + 0.5, y + 0.5);
  return out;
}

/** The dressing of the 256 m plains. */
export const PLAINS_DRESSING: readonly Dressed[] = build();
